import type React from "react";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItemText,
  ListItemButton,
  FormControl,
  IconButton,
  Alert,
  CircularProgress,
  Avatar,
  Tooltip,
  Checkbox,
  Autocomplete,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  AddCircleOutlineOutlined,
  Cancel,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import type { IUser } from "../../../../types/callsheet";
import CustomTextField from "../../../../components/common/Textfield";
import { MuiTelInput } from "mui-tel-input";
import { INewUserData, IAddCrewModalProps } from "../../../../types/callsheet";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import apiService from "../../../../service/Api";
import { showErrorToast } from "../../../../components/common/ShowToast";

const AddCrewModal: React.FC<IAddCrewModalProps> = ({
  open,
  onClose,
  onAddCrew,
  onCreateUser,
  fetchCrewMembers,
  availableRoles,
  theme,
  colors,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newUserData, setNewUserData] = useState<INewUserData>({
    user: {
      email: "",
      first_name: "",
      last_name: "",
      username: "",
      individual: {
        phone_number: "",
      },
    },
    crew_member: {
      bio: "Pixel Pilot",
    },
  });

  const [existingUsers, setExistingUsers] = useState<IUser[]>([]);
  const reduxTheme = useSelector((state: RootState) => state.theme);

  useEffect(() => {
    if (searchTerm.trim().length < 3) {
      setExistingUsers([]);
      return;
    }

    const controller = new AbortController();

    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await apiService.get<{
          results: IUser[];
        }>(`/api/crews/crew-members/?search=${encodeURIComponent(searchTerm)}`);

        setExistingUsers(response.data.results);
      } catch (err: any) {
        if (err.name !== "CanceledError") {
          setError("Failed to fetch users");
        }
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(fetchUsers, 400);

    return () => {
      clearTimeout(debounce);
      controller.abort();
    };
  }, [searchTerm]);

  const handleUserSelect = (user: IUser) => {
    setSelectedUser(user);
    setShowCreateForm(false);
    setError(null);
  };

  const handleCreateNew = () => {
    setShowCreateForm(true);
    setSelectedUser(null);
    setError(null);
  };
  const handleCreateUser = async () => {
    if (
      !newUserData.user.first_name ||
      !newUserData.user.last_name ||
      !newUserData.user.email
    ) {
      showErrorToast("Please fill in all required fields");
      return;
    }

    const username =
      `${newUserData.user.first_name}.${newUserData.user.last_name}`.toLowerCase();

    setLoading(true);

    try {
      const createdUser = await onCreateUser({
        ...newUserData,
        user: {
          ...newUserData.user,
          username,
        },
      });

      setSelectedUser(createdUser);
      setShowCreateForm(false);
    } catch (err: any) {
      showErrorToast(err.message || "Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCrew = async () => {
    if (!selectedUser || selectedRoles.length === 0) {
      setError("Please select a user and at least one role.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await Promise.all(
        selectedRoles.map(async (role) => {
          await onAddCrew(selectedUser, role);
        })
      );

      await fetchCrewMembers();

      handleClose();
    } catch (err) {
      console.error("Error adding crew member(s):", err);
      setError(
        "Failed to add all selected roles to the crew. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSearchTerm("");
    setSelectedUser(null);
    setSelectedRoles([]);
    setShowCreateForm(false);
    setError(null);
    setNewUserData({
      user: {
        email: "",
        first_name: "",
        last_name: "",
        individual: {
          phone_number: "",
        },
      },
      crew_member: {
        bio: "Pixel Pilot",
      },
    });

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: "90vh" } }}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <PersonAddIcon sx={{ color: reduxTheme.primary_colors[100] }} />
          <Typography
            variant="h6"
            sx={{ color: reduxTheme.primary_colors[100], flexGrow: 1 }}
          >
            {showCreateForm ? "Add New Crew Member" : "Search Member"}
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{ color: reduxTheme.primary_colors[100] }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <AnimatePresence mode="wait">
          {!showCreateForm ? (
            <motion.div
              key="search"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={{ mb: 3 }}>
                <CustomTextField
                  fullWidth
                  placeholder="Search existing crew members..."
                  helperText="Type at least 3 characters to search crew member"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon
                        sx={{ mr: 1, color: reduxTheme.primary_colors[100] }}
                      />
                    ),
                  }}
                />

                {searchTerm && (
                  <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                    {loading ? (
                      <Box display="flex" justifyContent="center" py={2}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : error ? (
                      <Typography color="error" textAlign="center" py={2}>
                        {error}
                      </Typography>
                    ) : existingUsers.length > 0 ? (
                      <List>
                        {existingUsers.map((user) => (
                          <motion.div
                            key={user.user.email}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <ListItemButton
                              onClick={() => handleUserSelect(user)}
                              selected={
                                selectedUser?.user.email === user.user.email
                              }
                              sx={{
                                borderRadius: 1,
                                mb: 1,
                                backgroundColor:
                                  selectedUser?.user.email === user.user.email
                                    ? reduxTheme.primary_colors[800]
                                    : "transparent",
                              }}
                            >
                              <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                                {user.user.first_name.charAt(0)}
                              </Avatar>
                              <ListItemText
                                primary={`${user.user.first_name} ${user.user.last_name}`}
                                secondary={user.user.email}
                              />
                            </ListItemButton>
                          </motion.div>
                        ))}
                      </List>
                    ) : (
                      <Box textAlign="center" py={2}>
                        <Typography color="text.secondary">
                          No users found matching "{searchTerm}"
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </motion.div>
          ) : (
            <motion.div
              key="create"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: reduxTheme.primary_colors[400] }}
                >
                  Create New Crew Member
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: "1fr 1fr",
                  }}
                >
                  <CustomTextField
                    label="First Name"
                    value={newUserData.user.first_name}
                    onChange={(e) =>
                      setNewUserData((prev) => ({
                        ...prev,
                        user: {
                          ...prev.user,
                          first_name: e.target.value,
                        },
                      }))
                    }
                    required
                  />
                  <CustomTextField
                    label="Last Name"
                    value={newUserData.user.last_name}
                    onChange={(e) =>
                      setNewUserData((prev) => ({
                        ...prev,
                        user: {
                          ...prev.user,
                          last_name: e.target.value,
                        },
                      }))
                    }
                    required
                  />
                </Box>
                <CustomTextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={newUserData.user.email}
                  onChange={(e) =>
                    setNewUserData((prev) => ({
                      ...prev,
                      user: {
                        ...prev.user,
                        email: e.target.value,
                      },
                    }))
                  }
                  required
                  sx={{ mt: 2 }}
                />
                <MuiTelInput
                  value={newUserData.user.individual.phone_number}
                  onChange={(value) =>
                    setNewUserData((prev) => ({
                      ...prev,
                      user: {
                        ...prev.user,
                        individual: {
                          ...prev.user.individual,
                          phone_number: value,
                        },
                      },
                    }))
                  }
                  defaultCountry="ZW"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      "& fieldset": {
                        border: `1px solid ${reduxTheme.primary_colors[300]}`,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: reduxTheme.primary_colors[400],
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: reduxTheme.primary_colors[300],
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: reduxTheme.primary_colors[400],
                    },
                    mt: 2,
                  }}
                />
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        {selectedUser && !showCreateForm && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Box
              sx={{
                p: 2,
                backgroundColor: colors.primary[900],
                borderRadius: 1,
                mb: 3,
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Selected Crew Member:
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar>{selectedUser.user.first_name[0]}</Avatar>
                <Box>
                  <Typography variant="body1">
                    {selectedUser.user.first_name} {selectedUser.user.last_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedUser.user.email}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </motion.div>
        )}

        {selectedUser && !showCreateForm && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Autocomplete
              multiple
              options={availableRoles}
              disableCloseOnSelect
              getOptionLabel={(option) => option}
              value={selectedRoles}
              onChange={(event, newValue) => setSelectedRoles(newValue)}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox style={{ marginRight: 8 }} checked={selected} />
                  {option}
                </li>
              )}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  label="Search and select roles"
                  placeholder="Roles"
                />
              )}
            />
          </FormControl>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        {showCreateForm ? (
          <>
            <Button
              onClick={() => setShowCreateForm(false)}
              disabled={loading}
              sx={{ backgroundColor: reduxTheme.primary_colors[800] }}
            >
              Back to Search
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateUser}
              disabled={loading}
              sx={{
                backgroundColor: reduxTheme.primary_colors[400],
              }}
            >
              {loading ? <CircularProgress size={20} /> : "Create User"}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="contained"
              onClick={handleAddToCrew}
              disabled={!selectedUser || !selectedRoles || loading}
              sx={{
                backgroundColor: reduxTheme.primary_colors[400],
              }}
            >
              Assign Crew Member
            </Button>
            <Tooltip title="Add new crew member" arrow>
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineOutlined />}
                onClick={handleCreateNew}
                sx={{
                  backgroundColor: reduxTheme.primary_colors[400],
                }}
              >
                Add New Crew Member
              </Button>
            </Tooltip>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddCrewModal;
